/* eslint-disable no-console */
class PubSub {
  events = {
    addToCart: "add-to-cart",
    draftOrderFailure: "draft-order-failure",
    draftOrderSuccess: "draft-order-success",
    updateCart: "update-cart",
    updateDOM: "update-dom",
    mobileNavForward: "mobile-nav-forward",
    mobileNavBack: "mobile-nav-back",
    mobileNavClose: "mobile-nav-close",
    miniCartOpened: "mini-cart-opened",
    pickupStoreChanged: "pickup-store-changed",
    bopisInventoryUpdated: "bopis-inventory-updated",
  };

  constructor() {
    this.eventBus = document.createElement("div");

    this.setUpTracking();
  }

  /**
   * Sets up the tracking of subscriptions for debugging
   */
  setUpTracking() {
    this.subscriptions = new Map();

    Object.values(this.events).forEach((eventName) => {
      this.subscriptions.set(eventName, []);
    });
  }

  /**
   * Subscribes to an event
   * @param {string} event - The name of the event to subscribe to
   * @param {Function} callback - The function to call when the event is triggered
   * @param {Element} element - The element that is subscribing to the event
   */
  subscribe(event, callback, element) {
    if (this.unknownEvent(event)) {
      console.warn(`PubSub: Unknown event "${event}"`);
      return;
    }

    const subscription = this.addEventListener(event, callback, element);

    this.addSubToEvent(event, subscription);
  }

  /**
   * Unsubscribes from an event
   * @param {string} event - The name of the event to unsubscribe from
   * @param {Function} callback - The function to unsubscribe
   */
  unsubscribe(event, callback) {
    if (this.unknownEvent(event)) {
      console.warn(`PubSub: Unknown event "${event}"`);
      return;
    }

    const subscription = this.removeEventListener(event, callback);

    if (!subscription) {
      console.warn(
        `PubSub: Subscription not found for event "${event}" and callback "${callback}"`,
      );
      return;
    }

    this.removeSubFromEvent(event, subscription);
  }

  /**
   * Publishes an event to the event bus
   * @param {string} event - The name of the event to publish
   * @param {object} data - The data to publish
   */
  publish(event, data = {}) {
    if (this.unknownEvent(event)) {
      console.warn(`PubSub: Unknown event "${event}"`);
      return;
    }

    const customEvent = new CustomEvent(event, { detail: data });
    this.eventBus.dispatchEvent(customEvent);
  }

  /**
   * Checks if an event exists in the events object
   * @param {string} event - The name of the event to check
   * @returns {boolean} True if the event is unknown, false otherwise
   */
  unknownEvent(event) {
    return !Object.values(this.events).includes(event);
  }

  /**
   * Adds an event listener to the event bus
   * @param {string} event - The name of the event to listen for
   * @param {Function} callback - The function to call when the event is triggered
   * @param {Element} element - The element to add the event listener to
   * @returns {object} The subscription object
   */
  addEventListener(event, callback, element) {
    this.eventBus.addEventListener(event, callback);

    return {
      event,
      callback,
      element,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Removes an event listener from the event bus
   * @param {string} event - The name of the event to remove the listener from
   * @param {Function} callback - The function to remove the listener from
   * @returns {object} The subscription object
   */
  removeEventListener(event, callback) {
    this.eventBus.removeEventListener(event, callback);

    const subsForEvent = this.subscriptions.get(event);
    const subscription = subsForEvent.find((sub) => sub.callback === callback);

    return subscription;
  }

  /**
   * Adds a subscription to an event
   * @param {string} eventName - The name of the event to add the subscription to
   * @param {object} subscription - The subscription object to add
   */
  addSubToEvent(eventName, subscription) {
    const subscriptions = this.subscriptions.get(eventName);

    if (!subscriptions) {
      this.subscriptions.set(eventName, [subscription]);
    }

    subscriptions.push(subscription);
  }

  /**
   * Removes a subscription from an event
   * @param {string} eventName - The name of the event to remove the subscription from
   * @param {object} subscription - The subscription object to remove
   */
  removeSubFromEvent(eventName, subscription) {
    const subscriptions = this.subscriptions.get(eventName);
    subscriptions.splice(subscriptions.indexOf(subscription), 1);
  }
}

WYATT.PubSub = new PubSub();

export default PubSub;
